import { Channel } from "@redotech/congo-enums/channel";
import { ReturnableItem } from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import { getPrimaryProduct } from "./util";

/**
 * Point of sale has no quantity stepper, so a bulk-returned item there is all
 * or nothing. POS ignores the team's bulk returns setting and handles
 * quantities the way a team without the setting does.
 */
export function bulkReturnsEnabledForChannel(
  bulkReturnsEnabled: boolean | null | undefined,
  channel: Channel | null | undefined,
): boolean {
  return !!bulkReturnsEnabled && channel !== Channel.Pointofsale;
}

/**
 * Determines if an item can be bulk returned based on its collections
 * and the team's bulk returns settings.
 *
 * @param returnableItemOrCollections - The return item to check
 * @param settings - The return app settings containing bulk returns configuration
 * @returns true if the item can be bulk returned, false otherwise
 */
export function canBulkReturnItem(
  returnableItemOrCollections: ReturnableItem | string[],
  bulkReturnsEnabled?: boolean,
  bulkReturnsCollections?: string[],
): boolean {
  // If bulk returns is not enabled, item cannot be bulk returned
  if (!bulkReturnsEnabled) {
    return false;
  }

  // If no collections are specified, all products can be bulk returned
  if (!bulkReturnsCollections?.length) {
    return true;
  }

  let collections: string[] = [];

  if (Array.isArray(returnableItemOrCollections)) {
    collections = returnableItemOrCollections;
  } else {
    const primaryProduct = getPrimaryProduct(returnableItemOrCollections);
    if (!primaryProduct?.collections?.length) {
      return false;
    }
    collections = primaryProduct.collections;
  }

  // Check if any of the item's collections are in the bulk returns collections list
  return collections.some((collectionName) =>
    bulkReturnsCollections?.includes(collectionName),
  );
}

/**
 * Determines if quantity controls should be hidden for an item based on bulk returns settings.
 *
 * @param returnableItemOrCollections - The return item to check
 * @param settings - The return app settings containing bulk returns configuration
 * @returns true if quantity controls should be hidden, false otherwise
 */
export function shouldHideQuantityForBulkReturns(
  returnableItemOrCollections: ReturnableItem | string[],
  bulkReturnsEnabled?: boolean,
  bulkReturnsCollections?: string[],
  bulkReturnsHideQuantity?: boolean,
): boolean {
  // Only hide quantity if the item can be bulk returned and hideQuantity is enabled
  return (
    canBulkReturnItem(
      returnableItemOrCollections,
      bulkReturnsEnabled,
      bulkReturnsCollections,
    ) &&
    (bulkReturnsHideQuantity ?? false)
  );
}
